/**
 * DragDropPuzzle — Drag-and-drop interaction component.
 *
 * Props:
 *   variant    — 'categorise' | 'place-on-diagram'
 *   items      — array of { id, label, correctZone, icon?, hint? }
 *   zones      — array of { id, label, colour? }
 *                For 'place-on-diagram': zones also need { x, y, w, h } (% of diagram area)
 *   prompt?    — instruction string (overrides the default instruction line)
 *   score?     — total points for completing (displayed in success state)
 *   onComplete — () => void — called when all items are correctly placed
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer,
  faMicrochip,
  faShieldHalved,
  faDesktop,
  faDatabase,
  faGaugeHigh,
  faTowerBroadcast,
} from '@fortawesome/free-solid-svg-icons';

const ICON_MAP = {
  faServer,
  faMicrochip,
  faShieldHalved,
  faDesktop,
  faDatabase,
  faGaugeHigh,
  faTowerBroadcast,
};

// Zone colour palette — used by ColumnDropZone and placed-item chips.
const ZONE_COLOURS = {
  blue: {
    idle: 'border-blue-500/40 bg-blue-950/30',
    over: 'border-blue-400 bg-blue-900/30 shadow-inner shadow-blue-900/20',
    header: 'text-blue-300',
    headerBorder: 'border-blue-500/30',
    chip: 'border-blue-400/60 bg-blue-900/40 text-blue-200',
    empty: 'text-blue-400/30',
  },
  amber: {
    idle: 'border-amber-500/40 bg-amber-950/30',
    over: 'border-amber-400 bg-amber-900/30 shadow-inner shadow-amber-900/20',
    header: 'text-amber-300',
    headerBorder: 'border-amber-500/30',
    chip: 'border-amber-400/60 bg-amber-900/40 text-amber-200',
    empty: 'text-amber-400/30',
  },
  default: {
    idle: 'border-cyber-muted/50 bg-cyber-panel/30',
    over: 'border-accent bg-cyber-dim/40 shadow-inner',
    header: 'text-cyber-faint',
    headerBorder: 'border-cyber-muted/30',
    chip: 'border-safe/60 bg-green-900/30 text-safe',
    empty: 'text-cyber-muted/40',
  },
};

// ─── DraggableCard ────────────────────────────────────────────────────────────
function DraggableCard({ item, isShaking, isPlaced, compact = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    disabled: isPlaced,
  });

  const style = { touchAction: 'none' };
  const icon = item.icon ? ICON_MAP[item.icon] : null;

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      animate={isShaking ? { x: [0, -12, 12, -8, 8, 0] } : { x: 0 }}
      transition={isShaking ? { duration: 0.45 } : { duration: 0.1 }}
      className={`select-none ${isPlaced ? 'invisible pointer-events-none' : ''} ${isDragging ? 'opacity-20' : ''}`}
      {...listeners}
      {...attributes}
    >
      <div
        className={[
          'flex flex-col items-center gap-1.5 rounded-xl border-2 font-mono transition-all',
          compact
            ? 'px-3 py-2.5 text-xs min-w-[108px]'
            : 'px-4 py-3 text-sm min-w-[148px] max-w-[160px]',
          isDragging
            ? 'border-accent bg-cyber-dim shadow-xl shadow-black/60 cursor-grabbing'
            : 'border-accent/40 bg-gradient-to-b from-slate-800/80 to-slate-900/80 text-cyber-text hover:border-accent/70 hover:shadow-md hover:shadow-accent/10 hover:from-slate-800 hover:to-slate-900 cursor-grab',
        ].join(' ')}
      >
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            className={[
              'opacity-75',
              compact ? 'text-base text-accent/80' : 'text-xl text-accent',
            ].join(' ')}
          />
        )}
        <span className="font-bold text-center leading-tight text-[0.8rem]">
          {item.label}
        </span>
        {item.hint && !compact && (
          <p className="text-[0.68rem] text-cyber-faint/70 text-center leading-snug mt-0.5">
            {item.hint}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// ─── ColumnDropZone (Variant A) ───────────────────────────────────────────────
function ColumnDropZone({ zone, placedItems }) {
  const { setNodeRef, isOver } = useDroppable({ id: zone.id });
  const colours = ZONE_COLOURS[zone.colour] ?? ZONE_COLOURS.default;

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex-1 flex flex-col gap-3 rounded-xl border-2 min-h-[160px] min-w-0 transition-all overflow-hidden',
        isOver ? colours.over : colours.idle,
      ].join(' ')}
    >
      {/* Zone header */}
      <div className={`px-4 pt-3 pb-2 border-b ${colours.headerBorder}`}>
        <span className={`text-xs font-mono tracking-widest uppercase font-bold ${colours.header}`}>
          {zone.label}
        </span>
      </div>

      {/* Placed items */}
      <div className="flex flex-col gap-2 px-3 pb-3 flex-1">
        <AnimatePresence>
          {placedItems.map((item) => {
            const icon = item.icon ? ICON_MAP[item.icon] : null;
            return (
              <motion.div
                key={item.id}
                initial={{ scale: 0.8, opacity: 0, y: -8 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border ${colours.chip} font-mono text-xs font-bold`}
              >
                {icon && <FontAwesomeIcon icon={icon} className="opacity-70 shrink-0" />}
                <span className="flex-1 leading-tight">{item.label}</span>
                <span className="text-green-400 shrink-0">✓</span>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {placedItems.length === 0 && (
          <div className={`flex-1 flex items-center justify-center text-xs font-mono italic ${colours.empty}`}>
            {isOver ? 'release to drop' : 'drag items here'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ScadaDiagram — SVG background for the place-on-diagram layout ───────────
function ScadaDiagram({ zones }) {
  const TEAL  = '#5be8c8';
  const DIM   = '#2a4060';
  const LABEL = '#7ab0d0';

  const z = Object.fromEntries((zones ?? []).map((zone) => [zone.id, zone]));

  const cx  = (id) => (z[id]?.x ?? 0) + (z[id]?.w ?? 0) / 2;
  const bot = (id) => (z[id]?.y ?? 0) + (z[id]?.h ?? 0);
  const top = (id) => z[id]?.y ?? 0;

  const busY    = (bot('slot_scada') + top('slot_plc')) / 2;
  const busMidX = (cx('slot_scada') + cx('slot_hmi')) / 2;
  const sep1Y   = busY;
  const sep2Y   = (bot('slot_plc') + top('slot_rtu')) / 2;

  return (
    <svg
      viewBox="0 0 100 100"
      width="100%"
      height="100%"
      style={{ display: 'block', background: '#07111f' }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <marker
          id="ddArrow"
          markerWidth="3" markerHeight="3"
          refX="3" refY="1.5"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0,0 L3,1.5 L0,3 z" fill={TEAL} />
        </marker>
      </defs>

      {/* Level separator lines */}
      <line x1="14" y1={sep1Y} x2="99" y2={sep1Y} stroke={DIM} strokeWidth="0.3" strokeDasharray="1 1" />
      <line x1="14" y1={sep2Y} x2="99" y2={sep2Y} stroke={DIM} strokeWidth="0.3" strokeDasharray="1 1" />

      {/* IEC 61850 level labels */}
      <text x="0.5" y={sep1Y / 2 - 2} fontSize="3" fill={LABEL} fontFamily="monospace">L2</text>
      <text x="0.5" y={sep1Y / 2 + 2} fontSize="3" fill={LABEL} fontFamily="monospace">Station</text>
      <text x="0.5" y={(sep1Y + sep2Y) / 2 - 2} fontSize="3" fill={LABEL} fontFamily="monospace">L1</text>
      <text x="0.5" y={(sep1Y + sep2Y) / 2 + 2} fontSize="3" fill={LABEL} fontFamily="monospace">Bay</text>
      <text x="0.5" y={(sep2Y + 100) / 2 - 2} fontSize="3" fill={LABEL} fontFamily="monospace">L0</text>
      <text x="0.5" y={(sep2Y + 100) / 2 + 2} fontSize="3" fill={LABEL} fontFamily="monospace">Process</text>

      {/* Connections */}
      <line x1={cx('slot_scada')} y1={bot('slot_scada')} x2={cx('slot_scada')} y2={busY} stroke={TEAL} strokeWidth="0.8" />
      <line x1={cx('slot_hmi')}   y1={bot('slot_hmi')}   x2={cx('slot_hmi')}   y2={busY} stroke={TEAL} strokeWidth="0.8" />
      <line x1={cx('slot_scada')} y1={busY} x2={cx('slot_hmi')} y2={busY} stroke={TEAL} strokeWidth="0.8" />
      <line x1={busMidX} y1={busY} x2={cx('slot_plc')} y2={top('slot_plc')} stroke={TEAL} strokeWidth="0.8" markerEnd="url(#ddArrow)" />
      <line x1={cx('slot_plc')} y1={bot('slot_plc')} x2={cx('slot_rtu')} y2={top('slot_rtu')} stroke={TEAL} strokeWidth="0.8" markerEnd="url(#ddArrow)" />
    </svg>
  );
}

// ─── HotspotDropZone (Variant B) ─────────────────────────────────────────────
function HotspotDropZone({ zone, placedItem }) {
  const { setNodeRef, isOver } = useDroppable({ id: zone.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        position: 'absolute',
        left: `${zone.x}%`,
        top: `${zone.y}%`,
        width: `${zone.w}%`,
        height: `${zone.h}%`,
      }}
      className={[
        'rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all overflow-hidden',
        placedItem
          ? 'border-safe bg-green-900/50 shadow-sm shadow-green-500/20'
          : isOver
          ? 'border-accent border-dashed bg-accent/10 shadow-md shadow-accent/20'
          : 'border-cyan-700/40 border-dashed bg-slate-900/40 hover:border-cyan-600/60',
      ].join(' ')}
    >
      {placedItem ? (
        <motion.div
          key={placedItem.id}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 22 }}
          className="relative w-full h-full flex flex-col items-center justify-center gap-1 px-1"
        >
          {placedItem.icon && ICON_MAP[placedItem.icon] && (
            <FontAwesomeIcon
              icon={ICON_MAP[placedItem.icon]}
              className="text-safe text-sm opacity-80"
            />
          )}
          <span className="text-safe font-mono text-[0.65rem] font-bold text-center leading-tight z-10">
            {placedItem.label}
          </span>
          <motion.div
            className="absolute inset-0 rounded-lg pointer-events-none"
            initial={{ boxShadow: '0 0 0 0px rgba(34,197,94,0)' }}
            animate={{ boxShadow: '0 0 0 2px #22c55e, 0 0 10px rgba(34,197,94,0.3)' }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </motion.div>
      ) : (
        <span className={`text-[0.55rem] font-mono ${isOver ? 'text-accent' : 'text-cyan-700/50'}`}>
          {isOver ? 'drop' : '···'}
        </span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function DragDropPuzzle({
  variant = 'categorise',
  items = [],
  zones = [],
  prompt,
  score,
  onComplete,
}) {
  const [placed, setPlaced] = useState({});
  const [shakingItemId, setShakingItemId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  const lastDropCorrectRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const activeItem = items.find((item) => item.id === activeId) ?? null;

  const handleDragStart = useCallback(({ active }) => {
    setActiveId(active.id);
  }, []);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      const item = items.find((i) => i.id === active.id);
      const isCorrect =
        !!over && !!item && !placed[item.id] && item.correctZone === over.id;
      lastDropCorrectRef.current = isCorrect;

      setActiveId(null);
      if (!over || !item || placed[item.id]) return;

      if (isCorrect) {
        const newPlaced = { ...placed, [item.id]: over.id };
        setPlaced(newPlaced);
        setFeedback({ type: 'success', text: `${item.label} — correct placement.` });

        const allCorrect = items.every((i) => newPlaced[i.id]);
        if (allCorrect) {
          setIsComplete(true);
          setTimeout(() => onComplete?.(), 700);
        }
      } else {
        setShakingItemId(item.id);
        setFeedback({ type: 'error', text: `Wrong zone. Try again.` });
        setTimeout(() => setShakingItemId(null), 500);
      }
    },
    [items, placed, onComplete]
  );

  // ── Variant A: categorise layout ──────────────────────────────────────────
  const renderVariantA = () => (
    <div className="flex flex-col gap-5 w-full">
      <p className="text-cyber-faint text-sm font-mono text-center">
        {prompt ?? 'Drag each item to the correct zone'}
      </p>

      {/* Drop zone columns */}
      <div className="flex gap-4">
        {zones.map((zone) => (
          <ColumnDropZone
            key={zone.id}
            zone={zone}
            placedItems={items.filter((item) => placed[item.id] === zone.id)}
          />
        ))}
      </div>

      {/* Item pool */}
      <div className="flex flex-wrap justify-center gap-3 min-h-[56px]">
        {items.map((item) => (
          <DraggableCard
            key={item.id}
            item={item}
            isShaking={shakingItemId === item.id}
            isPlaced={!!placed[item.id]}
          />
        ))}
      </div>
    </div>
  );

  // ── Variant B: place-on-diagram layout ────────────────────────────────────
  const renderVariantB = () => (
    <div className="flex gap-4 w-full flex-1 min-h-0">
      {/* Toolbox panel */}
      <div className="w-36 flex-shrink-0 flex flex-col gap-3">
        <p className="text-cyber-faint text-xs font-mono uppercase tracking-wider mb-1">
          Toolbox
        </p>
        {items.map((item) => (
          <DraggableCard
            key={item.id}
            item={item}
            isShaking={shakingItemId === item.id}
            isPlaced={!!placed[item.id]}
            compact
          />
        ))}
      </div>

      {/* Diagram with hotspot drop zones */}
      <div className="relative flex-1 min-h-[480px] rounded-xl overflow-hidden border border-cyan-900/40" style={{ background: '#07111f' }}>
        <div className="absolute inset-0">
          <ScadaDiagram zones={zones} />
        </div>
        {zones.map((zone) => (
          <HotspotDropZone
            key={zone.id}
            zone={zone}
            placedItem={
              items.find(
                (i) => placed[i.id] === zone.id && i.correctZone === zone.id
              ) ?? null
            }
          />
        ))}
      </div>
    </div>
  );

  const isHotspotLayout =
    variant === 'place-on-diagram' &&
    zones.length > 0 &&
    zones[0].x !== undefined;

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {isHotspotLayout ? renderVariantB() : renderVariantA()}

        {/* Drag overlay */}
        <DragOverlay dropAnimation={lastDropCorrectRef.current ? null : undefined}>
          {activeItem && (
            <div className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border-2 border-accent bg-slate-800 text-cyber-text font-mono text-sm font-bold text-center shadow-2xl shadow-black/60 cursor-grabbing select-none ring-1 ring-accent/30">
              {activeItem.icon && ICON_MAP[activeItem.icon] && (
                <FontAwesomeIcon icon={ICON_MAP[activeItem.icon]} className="text-accent text-xl opacity-80" />
              )}
              <span className="text-[0.8rem] leading-tight">{activeItem.label}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Feedback banner */}
      <AnimatePresence>
        {feedback && !isComplete && (
          <motion.div
            key={feedback.text}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className={[
              'text-center text-sm font-mono px-4 py-2 rounded-lg border',
              feedback.type === 'success'
                ? 'border-safe/40 bg-green-900/20 text-safe'
                : 'border-critical/40 bg-red-900/20 text-critical',
            ].join(' ')}
          >
            {feedback.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion state */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', stiffness: 250, damping: 18 }}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-safe bg-green-900/20 text-safe font-mono text-center"
          >
            <div className="text-lg font-bold">All items classified correctly!</div>
            {score !== undefined && (
              <div className="text-sm opacity-80">+{score} Defense Score</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default DragDropPuzzle;
